import { z } from "zod";

// Base schema for common fields
const baseRegisterSchema = {
  firstName: z
    .string()
    .trim() // Trim whitespace
    .min(1, "First name is required")
    .max(50, "First name cannot exceed 50 characters")
    .regex(
      /^[a-zA-Z\s'-]+$/,
      "Invalid firstname" // Simplified error message
    ),
  lastName: z
    .string()
    .trim() // Trim whitespace
    .min(1, "Last name is required")
    .max(50, "Last name cannot exceed 50 characters")
    .regex(
      /^[a-zA-Z\s'-]+$/,
      "Invalid lastname" // Simplified error message
    ),
  email: z
    .string()
    .trim() // Trim whitespace
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(100, "Email cannot exceed 100 characters")
    .transform((val) => val.toLowerCase()),
  phoneNumber: z
    .string()
    .min(1, "Please confirm your phone number")
    // Allow spaces, hyphens, parentheses for input
    .refine(
      (val) => /^\+?[\d\s-()+]+$/.test(val),
      "Please enter a valid phone number"
    )
    // Clean up number to just digits and + for storage
    .transform((val) => val.replace(/[^\d+]/g, ""))
    // Validate cleaned number
    .refine(
      (val) => /^\+?\d{10,11}$/.test(val),
      "Phone number must be 10-11 digits"
    )
    // Ensure we only store digits
    .transform((val) => val.replace(/\+/g, "")),
};

// Schema for squeeze page (uses phone number as password)
export const squeezeRegisterSchema = z.object({
  ...baseRegisterSchema,
});

export type SqueezeRegisterFormData = z.infer<typeof squeezeRegisterSchema>;

export const loginSchema = z.object({
  email: z
    .string()
    .trim() // Trim whitespace
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(100, "Email cannot exceed 100 characters")
    .transform((val) => val.toLowerCase()),
  password: z.string().min(1, "Password is required"),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Schema for regular registration (uses separate password field)
export const registerSchema = z.object({
  ...baseRegisterSchema,
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export type RegisterFormData = z.infer<typeof registerSchema>;
